import React, { SyntheticEvent, useCallback, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import Form, {
    Field,
    FormSection,
    FormFooter,
    ErrorMessage,
    Legend
} from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Box, Flex, Stack, xcss } from "@atlaskit/primitives";
import { Text } from "@atlaskit/primitives-next";
import TextField from "@atlaskit/textfield";
import { token } from "@atlaskit/tokens";

import { getFirstName } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { ANALYTICS_PREFIX } from "../../../common/constants";
import { PageProjectDetailsProps } from "../../../common/types";
import { useWizardConfiguration } from "../../../controllers";
import { useIsTeamTypeSpaceRecommendationExperiment } from "../../../hooks/experiment-hooks";

import {
    JIRA_MAX_PROJECT_NAME_LENGTH,
    JIRA_MIN_PROJECT_NAME_LENGTH,
    PROJECT_NAME_TEXT_FIELD
} from "./constants";
import messages from "./messages";
import { getTeamSpecificExamples } from "./utils";

const getTitleMessage = (isVisualUpdate: boolean) =>
    isVisualUpdate ? messages.titleVisualUpdate : messages.title;

const getSubtextMessage = (isSpaceExperiment: boolean) =>
    !isSpaceExperiment ? messages.bodyLineOne : messages.bodyLineOneVariation;

const getFieldLabelMessage = (
    isVisualUpdate: boolean,
    isSpaceExperiment: boolean
) => {
    if (isVisualUpdate) return messages.projectFormFieldVisualUpdate;
    if (!isSpaceExperiment) return messages.projectFormField;
    return messages.projectFormFieldVariation;
};

const getLegendMessage = (
    isVisualUpdate: boolean,
    isSpaceExperiment: boolean
) => {
    if (isVisualUpdate) return messages.projectExampleHeadingVisualUpdate;
    if (!isSpaceExperiment) return messages.projectExampleHeading;
    return messages.projectExampleHeadingVariation;
};

const getProjectExampleStackStyles = (
    isVisualUpdate: boolean,
    isSpaceExperiment: boolean
) => {
    if (isSpaceExperiment) return undefined;
    return isVisualUpdate
        ? projectExampleVisualUpdateStyles
        : projectExampleStyles;
};

const getProjectExampleStackSpace = (
    isVisualUpdate: boolean,
    isSpaceExperiment: boolean
) => {
    if (isSpaceExperiment) return undefined;
    return isVisualUpdate ? ("space.0" as const) : ("space.100" as const);
};

export const LeftHandSidePageProjectDetails: React.FC<
    PageProjectDetailsProps
> = ({
    onClickNext,
    currentProjectName,
    onProjectNameChange,
    defaultProjectName,
    submitButton,
    Header
}) => {
    const { formatMessage } = useIntl();
    const loggedInUser = useLoggedInUser();
    const userName = loggedInUser.name ? getFirstName(loggedInUser.name) : "";

    const isTeamTypeSpaceRecommendationExperiment: boolean =
        useIsTeamTypeSpaceRecommendationExperiment();
    const modalVisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );
    const { teamType } = useWizardConfiguration();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    // State for managing selected example button
    const [selectedExampleIndex, setSelectedExampleIndex] = useState(0);
    const [hasClickedExample, setHasClickedExample] = useState(false);

    const handleOnSubmit = () => {
        const teamSpecificExamples = getTeamSpecificExamples(teamType);
        const selectedExampleText =
            teamSpecificExamples[selectedExampleIndex]?.defaultMessage;

        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: `${ANALYTICS_PREFIX}PageProjectDetailsSubmitButton`,
            eventType: "ui",
            attributes: {
                teamType,
                isTeamTypeSpaceRecommendationExperiment,
                hasClickedExample,
                selectedExampleIndex,
                selectedExampleText,
                submittedTextMatchesExample:
                    currentProjectName === selectedExampleText
            }
        }).fire();

        onClickNext();
    };

    const handleExampleClick = useCallback(
        (exampleText: string, index: number) => {
            setSelectedExampleIndex(index);
            setHasClickedExample(true);
            onProjectNameChange(exampleText);
        },
        [onProjectNameChange]
    );

    const validateProjectName = useCallback(
        (value: string | undefined) => {
            // If the project name is undefined, we don't need to validate it since we use the default project name
            if (value === undefined) {
                return;
            }
            const trimmedValueLength = value?.trim().length ?? 0;
            if (trimmedValueLength < JIRA_MIN_PROJECT_NAME_LENGTH) {
                return formatMessage(
                    messages.projectNameMinCharacterLengthError,
                    {
                        _minCharacters: JIRA_MIN_PROJECT_NAME_LENGTH,
                        get minCharacters() {
                            return this["_minCharacters"];
                        },
                        set minCharacters(value) {
                            this["_minCharacters"] = value;
                        }
                    }
                );
            }
            if (trimmedValueLength > JIRA_MAX_PROJECT_NAME_LENGTH) {
                return formatMessage(
                    messages.projectNameMaxCharacterLengthError,
                    {
                        maxCharacters: JIRA_MAX_PROJECT_NAME_LENGTH
                    }
                );
            }
        },
        [formatMessage]
    );

    const handleProjectNameChanged = useCallback(
        (onChange: (e: SyntheticEvent<HTMLInputElement>) => void) =>
            (e: SyntheticEvent<HTMLInputElement>) => {
                onProjectNameChange(e.currentTarget.value);
                onChange(e);
            },
        [onProjectNameChange]
    );

    const FormContainer =
        checkFeatureFlagGate(featureFlags.jiraOnboardingAiMadlibV2) && Header
            ? Stack
            : FormSection;

    const renderHeading = () => {
        if (
            checkFeatureFlagGate(featureFlags.jiraOnboardingAiMadlibV2) &&
            Header
        ) {
            return (
                <Header
                    header={formatMessage(messages.spaceHeader)}
                    subheader={formatMessage(messages.bodyLineOneVariation)}
                />
            );
        }

        const isVisualUpdate =
            modalVisualUpdates && !isTeamTypeSpaceRecommendationExperiment;
        const spaceExperimentHeading = userName
            ? undefined
            : formatMessage(messages.titleVariationFallback);
        const headingText = !isTeamTypeSpaceRecommendationExperiment
            ? formatMessage(getTitleMessage(isVisualUpdate))
            : spaceExperimentHeading;

        const subtext = isVisualUpdate ? (
            <p css={subtextVisualUpdateStyles}>
                <FormattedMessage {...messages.bodyLineOneVisualUpdate} />
            </p>
        ) : (
            <Box xcss={subtextStyles}>
                <FormattedMessage
                    {...getSubtextMessage(
                        isTeamTypeSpaceRecommendationExperiment
                    )}
                />
            </Box>
        );

        return (
            <Stack space="space.150">
                <div css={[isVisualUpdate && titleVisualUpdateCss]}>
                    <Heading as="h1" size="large">
                        {headingText ??
                            (userName ? (
                                <FormattedMessage
                                    {...messages.titleVariation}
                                    values={{ userName }}
                                />
                            ) : (
                                formatMessage(messages.titleVariationFallback)
                            ))}
                    </Heading>
                </div>
                {subtext}
            </Stack>
        );
    };

    const renderProjectExamples = () => {
        const isVisualUpdate =
            modalVisualUpdates && !isTeamTypeSpaceRecommendationExperiment;
        const legendMessage = formatMessage(
            getLegendMessage(
                isVisualUpdate,
                isTeamTypeSpaceRecommendationExperiment
            )
        );

        const baseExamplesElement = isVisualUpdate ? (
            <p css={subtextVisualUpdateStyles}>
                {formatMessage(messages.projectExamples)}
            </p>
        ) : (
            <Text color="color.text.subtlest" as="p">
                {formatMessage(messages.projectExamples)}
            </Text>
        );

        const exampleContent = !isTeamTypeSpaceRecommendationExperiment ? (
            baseExamplesElement
        ) : (
            <Flex gap="space.100" wrap="wrap" justifyContent="start">
                {getTeamSpecificExamples(teamType).map((example, index) => {
                    const exampleText = formatMessage(example);
                    const isSelected =
                        (currentProjectName ?? "").trim() ===
                        exampleText.trim();
                    return (
                        <Button
                            key={teamType}
                            appearance="subtle"
                            isSelected={isSelected}
                            aria-pressed={isSelected}
                            onClick={() =>
                                handleExampleClick(exampleText, index)
                            }
                            css={[
                                buttonStyles,
                                isSelected && selectedButtonStyles,
                                !isSelected && unselectedButtonStyles
                            ]}
                        >
                            {exampleText}
                        </Button>
                    );
                })}
            </Flex>
        );

        return (
            <Stack
                xcss={getProjectExampleStackStyles(
                    isVisualUpdate,
                    isTeamTypeSpaceRecommendationExperiment
                )}
                space={getProjectExampleStackSpace(
                    isVisualUpdate,
                    isTeamTypeSpaceRecommendationExperiment
                )}
            >
                <div css={[isVisualUpdate && exampleHeadingVisualUpdateCss]}>
                    <Legend>{legendMessage}</Legend>
                </div>
                {exampleContent}
            </Stack>
        );
    };

    const renderSubmitButton = () => {
        if (submitButton) {
            return submitButton;
        }
        return (
            <div css={[modalVisualUpdates && submitButtonVisualUpdateCss]}>
                <Button type="submit" appearance="primary">
                    {formatMessage(messages.getStartedBtn)}
                </Button>
            </div>
        );
    };

    const isVisualUpdate =
        modalVisualUpdates && !isTeamTypeSpaceRecommendationExperiment;
    const fieldLabel = formatMessage(
        getFieldLabelMessage(
            isVisualUpdate,
            isTeamTypeSpaceRecommendationExperiment
        )
    );

    return (
        <Flex xcss={outerContainerStyles}>
            <Form onSubmit={handleOnSubmit}>
                {({ formProps }) => (
                    <form {...formProps} noValidate>
                        <Stack spread="space-between" xcss={containerStyles}>
                            <Stack>
                                {renderHeading()}
                                <FormContainer>
                                    <Stack space="space.300">
                                        <div
                                            css={[
                                                modalVisualUpdates &&
                                                    !isTeamTypeSpaceRecommendationExperiment &&
                                                    fieldLabelVisualUpdateCss
                                            ]}
                                        >
                                            <Field
                                                name={PROJECT_NAME_TEXT_FIELD}
                                                label={fieldLabel}
                                                validate={validateProjectName}
                                            >
                                                {({
                                                    fieldProps,
                                                    error,
                                                    meta
                                                }) => (
                                                    <>
                                                        <TextField
                                                            {...fieldProps}
                                                            autoComplete="off"
                                                            defaultValue={
                                                                defaultProjectName
                                                            }
                                                            value={
                                                                currentProjectName
                                                            }
                                                            onChange={handleProjectNameChanged(
                                                                fieldProps.onChange
                                                            )}
                                                        />
                                                        {fieldProps.isInvalid &&
                                                            meta.touched && (
                                                                <ErrorMessage>
                                                                    {error}
                                                                </ErrorMessage>
                                                            )}
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                        {renderProjectExamples()}
                                    </Stack>
                                </FormContainer>
                            </Stack>
                            <FormFooter>{renderSubmitButton()}</FormFooter>
                        </Stack>
                    </form>
                )}
            </Form>
        </Flex>
    );
};

const outerContainerStyles = xcss({
    height: "100%"
});
const containerStyles = xcss({
    height: "100%"
});

const subtextStyles = xcss({
    fontSize: "font.body.medium",
    color: "color.text.subtle"
});

const projectExampleStyles = xcss({
    backgroundColor: "color.background.input.hovered",
    borderRadius: "border.radius.100",
    padding: "space.100"
});

const projectExampleVisualUpdateStyles = xcss({
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "color.background.accent.gray.subtlest",
    borderRadius: "border.radius.100",
    paddingBlock: "space.150",
    paddingInline: "space.200"
});

const buttonStyles = css({
    borderWidth: "1px !important",
    // eslint-disable-next-line local/ensure-wac-token-usage
    borderColor: "#BCBDC0 !important",
    borderRadius: token("radius.small", "2px") + " !important",
    borderStyle: "solid !important",
    // Hard-coded values required because token values are being overridden / ignored
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        // Match the Text component font stack (ui-sans-serif, "Segoe UI", Ubuntu...)
        fontFamily:
            "ui-sans-serif, 'Segoe UI', Ubuntu, Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji' !important",
        fontSize: "14px !important",
        fontWeight: "bold !important"
    }
});

const unselectedButtonStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#626F86 !important"
    }
});

const selectedButtonStyles = css({
    borderColor: token("color.border.focused") + " !important"
});

// Visual update styles for cfind4831ProductOverlayModalVisualUpdates gate
// Only applied when gate is on and isTeamTypeSpaceRecommendationExperiment is false
const titleVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h1: {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        fontSize: "32px",
        lineHeight: "38px"
    }
});

const subtextVisualUpdateStyles = css({
    font: token("font.body"),
    color: token("color.text.subtle"),
    margin: 0
});

const exampleHeadingVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    legend: {
        font: token("font.body"),
        fontWeight: token("font.weight.bold")
    }
});

const submitButtonVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        margin: "0 !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontFamily: wacToken("font.charlie.text") + " !important",
        fontSize: "14px !important",
        lineHeight: "1.5rem !important",
        fontWeight: `${token("font.weight.medium")} !important`,
        textAlign: "center",
        margin: "0 !important"
    }
});

const fieldLabelVisualUpdateCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    label: {
        font: token("font.body.small"),
        fontWeight: token("font.weight.medium")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    input: {
        font: token("font.body"),
        color: token("color.text.subtlest")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    div: {
        borderColor: `${token("color.border.input")} !important`
    }
});
